import { createSlice } from "@reduxjs/toolkit";

import { requestAlias } from "./thunks/requestAlias";

import { requestSize } from "./thunks/requestSize";
import { requestSizeUpdate } from "./thunks/requestSizeUpdate";
import { requestDesign_Aggregated } from "./thunks/requestDesign_Aggregated";
import { requestDesignUpdate } from "./thunks/requestDesignUpdate";
import { requestSummary_Aggregated } from "./thunks/requestSummary_Aggregated";
import { requestEmailUterum } from "./thunks/requestEmailUterum";
import { requestAddToCart_Aggregated } from "./thunks/requestAddToCart_Aggregated";
import { requestPartPaymentWidget } from "./thunks/requestPartPaymentWidget";

const getPimClient = (channelId) => {
  switch (channelId) {
    case "1":
      return "Scensum";
    case "4":
      return "ScensumNo";
    case "7":
      return "ScensumNr_sv";
    case "8":
      return "ScensumNr_fi";
    default:
      return "";
  }
};

const initialState = {
  apiConfigs: {
    pimClient: "",
    cgeBaseUrl: "",
    scensumBaseUrl: "",
    skbvApiBaseUrl: "",
  },
  status: {
    requestAlias: "idle",
    requestSize: "idle",
    requestSizeUpdate: "idle",
    requestDesign_Aggregated: "idle",
    requestDesignUpdate: "idle",
    requestSummary_Aggregated: "idle",
    requestEmailUterum: "idle",
    requestAddToCart_Aggregated: "idle",
    requestPartPaymentWidget: "idle",
  },
};

const requestsSlice = createSlice({
  name: "requests",
  initialState,
  reducers: {
    setApiConfigs(state, action) {
      const { channelId, cgeBaseUrl, scensumBaseUrl, skbvApiBaseUrl } = action.payload;
      const pimClient = getPimClient(channelId);

      if (!pimClient || !cgeBaseUrl || !scensumBaseUrl || !skbvApiBaseUrl) {
        throw new Error(
          "Missing required API configuration in setApiConfigs action payload"
        );
      }

      state.apiConfigs = {
        pimClient,
        cgeBaseUrl,
        scensumBaseUrl,
        skbvApiBaseUrl,
      };
    },
    setRequestsAsIdle(state, action) {
      action.payload.forEach((request) => {
        if (state.status[request]) {
          state.status[request] = "idle";
        } else {
          console.error(
            "Unexpected request for status update in setRequestsAsIdle()"
          );
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      //requestAlias
      .addCase(requestAlias.pending, (state) => {
        state.status.requestAlias = "pending";
      })
      .addCase(requestAlias.fulfilled, (state) => {
        state.status.requestAlias = "fulfilled";
      })
      .addCase(requestAlias.rejected, (state) => {
        state.status.requestAlias = "rejected";
      })

      //requestSize
      .addCase(requestSize.pending, (state) => {
        state.status.requestSize = "pending";
      })
      .addCase(requestSize.fulfilled, (state) => {
        state.status.requestSize = "fulfilled";
      })
      .addCase(requestSize.rejected, (state) => {
        state.status.requestSize = "rejected";
      })

      //requestSizeUpdate
      .addCase(requestSizeUpdate.pending, (state) => {
        state.status.requestSizeUpdate = "pending";
      })
      .addCase(requestSizeUpdate.fulfilled, (state) => {
        state.status.requestSizeUpdate = "fulfilled";
      })
      .addCase(requestSizeUpdate.rejected, (state) => {
        state.status.requestSizeUpdate = "rejected";
      })

      //requestDesign_Aggregated
      .addCase(requestDesign_Aggregated.pending, (state) => {
        state.status.requestDesign_Aggregated = "pending";
      })
      .addCase(requestDesign_Aggregated.fulfilled, (state) => {
        state.status.requestDesign_Aggregated = "fulfilled";
      })
      .addCase(requestDesign_Aggregated.rejected, (state) => {
        state.status.requestDesign_Aggregated = "rejected";
      })

      //requestDesignUpdate
      .addCase(requestDesignUpdate.pending, (state) => {
        state.status.requestDesignUpdate = "pending";
      })
      .addCase(requestDesignUpdate.fulfilled, (state) => {
        state.status.requestDesignUpdate = "fulfilled";
      })
      .addCase(requestDesignUpdate.rejected, (state) => {
        state.status.requestDesignUpdate = "rejected";
      })

      //requestSummary_Aggregated
      .addCase(requestSummary_Aggregated.pending, (state) => {
        state.status.requestSummary_Aggregated = "pending";
      })
      .addCase(requestSummary_Aggregated.fulfilled, (state) => {
        state.status.requestSummary_Aggregated = "fulfilled";
      })
      .addCase(requestSummary_Aggregated.rejected, (state) => {
        state.status.requestSummary_Aggregated = "rejected";
      })

      //requestEmailUterum
      .addCase(requestEmailUterum.pending, (state) => {
        state.status.requestEmailUterum = "pending";
      })
      .addCase(requestEmailUterum.fulfilled, (state) => {
        state.status.requestEmailUterum = "fulfilled";
      })
      .addCase(requestEmailUterum.rejected, (state) => {
        state.status.requestEmailUterum = "rejected";
      })

      //requestAddToCart_Aggregated
      .addCase(requestAddToCart_Aggregated.pending, (state) => {
        state.status.requestAddToCart_Aggregated = "pending";
      })
      .addCase(requestAddToCart_Aggregated.fulfilled, (state) => {
        state.status.requestAddToCart_Aggregated = "fulfilled";
      })
      .addCase(requestAddToCart_Aggregated.rejected, (state) => {
        state.status.requestAddToCart_Aggregated = "rejected";
      })

      //requestPartPaymentWidget
      .addCase(requestPartPaymentWidget.pending, (state) => {
        state.status.requestPartPaymentWidget = "pending";
      })
      .addCase(requestPartPaymentWidget.fulfilled, (state) => {
        state.status.requestPartPaymentWidget = "fulfilled";
      })
      .addCase(requestPartPaymentWidget.rejected, (state) => {
        state.status.requestPartPaymentWidget = "rejected";
      });
  },
});

export const { setApiConfigs, setRequestsAsIdle } = requestsSlice.actions;

export default requestsSlice.reducer;
